// @soft-depth
import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import SoftDepthScope from '../../../design-system/SoftDepthScope';

/**
 * The segmented tab bar at the top of an IDEA dialog — a sliding pill over
 * equal-width tabs, with the WAI-ARIA tab keyboard pattern (←/→ wrap, Home/End).
 *
 * Shared by 招募 IDEA (候選人概覽 / 溝通方式) and Smart IDEA (客戶概覽 / 溝通方式).
 * Each host owns its panels; they should carry `id={`${idPrefix}-panel-${tab}`}`
 * and `aria-labelledby={`${idPrefix}-tab-${tab}`}` so the ARIA wiring matches.
 * `idPrefix` doubles as the test-id prefix.
 *
 * Soft Depth, business mode: the pill stays blue in 招募 IDEA, as it always was.
 */

export type IdeaTab<T extends string> = { id: T; label: string };

export interface IdeaTabsProps<T extends string> {
  tabs: IdeaTab<T>[];
  value: T;
  onChange: (next: T) => void;
  idPrefix: string;
  ariaLabel: string;
}

// 4px of padding around the tabs and between them.
const GAP = 4;

const IdeaTabs = <T extends string>(props: IdeaTabsProps<T>) => {
  const { tabs, value, onChange, idPrefix, ariaLabel } = props;
  const index = Math.max(
    0,
    tabs.findIndex(tab => tab.id === value)
  );
  const count = tabs.length;

  return (
    <SoftDepthScope mode="business">
      <Box
        role="tablist"
        aria-label={ariaLabel}
        data-active={value}
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
          gap: `${GAP}px`,
          width: 'min(100%, 380px)',
          p: `${GAP}px`,
          borderRadius: 1.5,
          bgcolor: 'grey.200'
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          data-testid={`${idPrefix}-tabs-pill`}
          sx={{
            position: 'absolute',
            top: GAP,
            bottom: GAP,
            left: GAP,
            width: `calc((100% - ${GAP * 2}px - ${GAP * (count - 1)}px) / ${count})`,
            borderRadius: 1,
            bgcolor: 'primary.main',
            boxShadow: 'none',
            transform: `translateX(calc((100% + ${GAP}px) * ${index}))`,
            transition: 'transform 180ms cubic-bezier(0.4, 0, 0.2, 1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
          }}
        />
        {tabs.map((tab, i) => {
          const selected = value === tab.id;
          return (
            <ButtonBase
              key={tab.id}
              id={`${idPrefix}-tab-${tab.id}`}
              type="button"
              role="tab"
              data-testid={`${idPrefix}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={selected ? `${idPrefix}-panel-${tab.id}` : undefined}
              tabIndex={selected ? 0 : -1}
              disableRipple
              onClick={() => onChange(tab.id)}
              onKeyDown={event => {
                const next =
                  event.key === 'ArrowRight'
                    ? (i + 1) % count
                    : event.key === 'ArrowLeft'
                    ? (i - 1 + count) % count
                    : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                    ? count - 1
                    : -1;
                if (next < 0) return;
                event.preventDefault();
                const nextTab = tabs[next].id;
                onChange(nextTab);
                document.getElementById(`${idPrefix}-tab-${nextTab}`)?.focus();
              }}
              sx={{
                position: 'relative',
                zIndex: 1,
                minHeight: 38,
                px: 1.5,
                py: 0.875,
                borderRadius: 1,
                typography: 'body2',
                fontWeight: 600,
                color: selected ? 'primary.contrastText' : 'grey.600',
                transition: 'color 120ms ease',
                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
              }}
            >
              {tab.label}
            </ButtonBase>
          );
        })}
      </Box>
    </SoftDepthScope>
  );
};

export default IdeaTabs;
